import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import ProjectCard from "../../components/common/ProjectCard";
import { allProjects, projectCategories, projectsData } from "../../config/constants/projectData";
import { getProjectUrl } from "../../utils/slugify";
import SEO from "../../components/common/SEO";
import SearchBar from "../../components/common/SearchBar";
import Breadcrumb from "../../components/common/Breadcrumb";
import Pagination from "../../components/common/Pagination";
import { FaSearch, FaClock, FaBehance, FaArrowRight } from "react-icons/fa";

// Helper Functions
const filterByCategory = (projects, category) => {
    if (category === "All") return projects;
    return projects.filter(project => project.category === category);
};

const searchProjects = (projects, query) => {
    if (!query.trim()) return projects;
    const lowerQuery = query.toLowerCase();
    return projects.filter(project =>
        project.title.toLowerCase().includes(lowerQuery)
    );
};

const sortProjects = (projects, sortOption) => {
    const sorted = [...projects];

    switch (sortOption) {
        case "newest":
            return sorted.sort((a, b) => {
                const dateA = new Date(projectsData.find(p => p.id === a.id)?.date || "January 2020");
                const dateB = new Date(projectsData.find(p => p.id === b.id)?.date || "January 2020");
                return dateB - dateA;
            });

        case "oldest":
            return sorted.sort((a, b) => {
                const dateA = new Date(projectsData.find(p => p.id === a.id)?.date || "January 2020");
                const dateB = new Date(projectsData.find(p => p.id === b.id)?.date || "January 2020");
                return dateA - dateB;
            });

        case "a-z":
            return sorted.sort((a, b) => a.title.localeCompare(b.title));

        case "z-a":
            return sorted.sort((a, b) => b.title.localeCompare(a.title));

        default:
            return sorted;
    }
};

const paginateProjects = (projects, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return projects.slice(startIndex, endIndex);
};

const calculateTotalPages = (totalItems, itemsPerPage) => {
    return Math.ceil(totalItems / itemsPerPage);
};

const padNumber = (n) => String(n).padStart(2, "0");

const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "a-z", label: "A-Z" },
    { value: "z-a", label: "Z-A" },
];

const Projects = () => {
    const [activeCategory, setActiveCategory] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOption, setSortOption] = useState("newest");
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const resultsRef = useRef(null);
    const ITEMS_PER_PAGE = 10;

    const handlePageChange = (nextPage) => {
        setCurrentPage(nextPage);
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        // Simulate loading delay
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 800);

        return () => clearTimeout(timer);
    }, []);

    // Computed derived state
    const processedProjects = useMemo(() => {
        // Step 1: Filter by category
        let result = filterByCategory(allProjects, activeCategory);

        // Step 2: Search
        result = searchProjects(result, searchQuery);

        // Step 3: Sort
        result = sortProjects(result, sortOption);

        return result;
    }, [activeCategory, searchQuery, sortOption]);

    // Pagination calculations
    const totalPages = calculateTotalPages(processedProjects.length, ITEMS_PER_PAGE);
    const displayedProjects = paginateProjects(processedProjects, currentPage, ITEMS_PER_PAGE);

    // Editorial lead story + the rest of the page
    const [leadProject, ...restProjects] = displayedProjects;
    const pageOffset = (currentPage - 1) * ITEMS_PER_PAGE;

    // Results info
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, processedProjects.length);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, searchQuery, sortOption]);

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Creative Projects - Arvix Creative Projects",
        "description": "Explore our creative projects featuring brand identity, UI/UX design, graphic design, and visual solutions. 120+ projects completed with 85+ happy clients.",
        "url": "https://arvixcreative.com/projects",
        "mainEntity": {
            "@type": "ItemList",
            "name": "Creative Projects",
            "description": "Collection of creative projects including branding, UI/UX, web development, and visual work",
            "numberOfItems": allProjects.length,
            "itemListElement": allProjects.map((project, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "CreativeWork",
                    "name": project.title,
                    "description": project.description,
                    "url": `https://arvixcreative.com/projects/${project.slug}`,
                    "image": project.image.startsWith("http") ? project.image : `https://arvixcreative.com${project.image}`,
                    "creator": {
                        "@type": "Organization",
                        "name": "Arvix Creative",
                        "alternateName": ["Tanvirus Pixels", "Tanvir Creative Studio"]
                    }
                }
            }))
        }
    };

    return (
        <>
            <SEO 
                title="Creative Projects - Arvix Creative Projects | Tanvirus Pixels"
                description="Explore our creative projects featuring brand identity, UI/UX design, graphic design, and visual solutions. 120+ projects completed with 85+ happy clients."
                keywords="arvix, arvix creative, creative projects, design projects, brand identity, UI/UX design, graphic design, project gallery, creative work, Bangladesh, Chattogram, tanvirus, tanvir, pixels, tanvirus pixels"
                url="https://arvixcreative.com/projects"
                structuredData={structuredData}
            />
            <div className="min-h-screen pt-24 pb-20 relative overflow-hidden bg-white">

            <div className="w-full relative z-10">
                <Breadcrumb items={[{ label: "Projects" }]} className="mb-8" />
                {/* Masthead */}
                <header className="mb-10 md:mb-14 animate-slide-down">
                    <div className="flex items-center gap-4 font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-dark/60">
                        <span className="text-designer-primary">The Archive</span>
                        <span className="h-px flex-1 bg-designer-dark/15" aria-hidden="true" />
                        <span>{allProjects.length} projects</span>
                        <span className="hidden sm:inline" aria-hidden="true">·</span>
                        <span className="hidden sm:inline">{projectCategories.length - 1} disciplines</span>
                    </div>
                    <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance">
                        Selected <em className="italic font-medium">work</em>
                    </h1>
                    <div className="mt-6 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                        <p className="max-w-xl text-base md:text-lg leading-relaxed text-gray-600">
                            Brand identities, campaigns, interfaces and packaging from the
                            studio floor.
                        </p>
                    </div>
                </header>

                {/* Studio note */}
                <div className="mb-10 md:mb-14 flex items-start sm:items-center gap-3 rounded-2xl border border-designer-dark/10 border-l-4 border-l-designer-primary bg-red-50/60 px-5 py-4 animate-fade-in">
                    <FaClock className="mt-0.5 sm:mt-0 h-4 w-4 shrink-0 text-designer-primary" aria-hidden="true" />
                    <p className="text-[15px] font-medium leading-relaxed text-designer-dark">
                        This gallery is just the surface. Dozens more projects are already delivered or still cooking in the studio.
                    </p>
                </div>

                {/* Control deck */}
                <div className="mb-10 md:mb-14 animate-fade-in rounded-3xl border border-designer-dark/10 bg-[#FBFAF9] p-4 sm:p-5 md:p-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="w-full sm:max-w-md sm:flex-1 lg:max-w-lg">
                            <SearchBar
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search the archive by title..."
                            />
                        </div>
                        <p className="font-mono text-xs text-gray-500 whitespace-nowrap sm:ml-auto" aria-live="polite">
                            {processedProjects.length > 0 ? (
                                <>
                                    <span className="font-bold text-designer-dark">{startIndex} to {endIndex}</span>
                                    {" "}of {processedProjects.length}
                                </>
                            ) : (
                                "No entries found"
                            )}
                        </p>
                    </div>

                    <div className="my-4 border-t border-designer-dark/10 sm:my-5" aria-hidden="true" />

                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
                        {/* Category pills */}
                        <nav className="flex flex-1 flex-wrap items-center gap-2" aria-label="Filter projects by category">
                            {projectCategories.map((category) => {
                                const projectCount = category === "All"
                                    ? allProjects.length
                                    : allProjects.filter(project => project.category === category).length;
                                const isActive = activeCategory === category;

                                return (
                                    <button
                                        key={category}
                                        onClick={() => setActiveCategory(category)}
                                        aria-pressed={isActive}
                                        className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200
                                            focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50 ${isActive
                                                ? "bg-designer-primary border-designer-primary text-white"
                                                : "bg-white border-designer-dark/10 text-gray-600 hover:border-designer-dark/30 hover:text-designer-dark"
                                            }`}
                                    >
                                        {category}
                                        <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none ${isActive ? "bg-white/20 text-white" : "bg-designer-dark/5 text-gray-500"}`}>
                                            {projectCount}
                                        </span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Sort segmented control */}
                        <div role="group" aria-label="Sort projects" className="inline-flex shrink-0 items-center gap-1 self-start rounded-full border border-designer-dark/10 bg-white p-1 xl:self-auto">
                            {SORT_OPTIONS.map((option) => {
                                const isActive = sortOption === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => setSortOption(option.value)}
                                        aria-pressed={isActive}
                                        className={`rounded-full px-3.5 sm:px-4 py-2 text-[13px] font-semibold transition-colors duration-200
                                            focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50 ${isActive
                                                ? "bg-designer-dark text-white"
                                                : "text-gray-500 hover:text-designer-dark"
                                            }`}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Active filter note */}
                {(activeCategory !== "All" || searchQuery) && processedProjects.length > 0 && (
                    <p className="mb-8 text-sm text-gray-600">
                        {activeCategory !== "All" && (
                            <span>Filed under <span className="font-semibold text-designer-dark">{activeCategory}</span></span>
                        )}
                        {activeCategory !== "All" && searchQuery && <span> · </span>}
                        {searchQuery && (
                            <span>matching “<span className="font-semibold text-designer-dark">{searchQuery}</span>”</span>
                        )}
                    </p>
                )}

                {/* Projects */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="w-12 h-12 border-4 border-designer-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : displayedProjects.length > 0 ? (
                    <>
                        <div ref={resultsRef} className="scroll-mt-24">
                        {/* Lead story */}
                        {leadProject && (
                            <article
                                className="group grid grid-cols-1 lg:grid-cols-12 gap-0 mb-10 md:mb-14 border border-designer-dark/10 rounded-3xl overflow-hidden bg-white"
                                itemScope
                                itemType="https://schema.org/CreativeWork"
                            >
                                <Link
                                    to={getProjectUrl(leadProject.slug)}
                                    className="lg:col-span-7 relative overflow-hidden min-h-[280px] lg:min-h-[420px]"
                                    aria-label={`View details for ${leadProject.title} project`}
                                    tabIndex={-1}
                                >
                                    <img
                                        src={leadProject.image}
                                        alt={`${leadProject.title} - ${leadProject.category} project by Arvix Creative`}
                                        loading="eager"
                                        decoding="async"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                    />
                                </Link>
                                <div className="lg:col-span-5 flex flex-col justify-center p-7 md:p-10 lg:border-l lg:border-designer-dark/10">
                                    <div className="flex items-center gap-3 font-mono text-[11px] font-bold tracking-[0.22em] uppercase">
                                        <span className="text-designer-primary">{padNumber(pageOffset + 1)}</span>
                                        <span className="h-px w-8 bg-designer-dark/20" aria-hidden="true" />
                                        <span className="text-gray-500" itemProp="genre">{leadProject.category}</span>
                                    </div>
                                    <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold tracking-tight text-designer-dark leading-tight" itemProp="name">
                                        {leadProject.title}
                                    </h2>
                                    <p className="mt-4 text-gray-600 leading-relaxed line-clamp-3" itemProp="description">
                                        {leadProject.description}
                                    </p>
                                    <Link
                                        to={getProjectUrl(leadProject.slug)}
                                        className="mt-7 inline-flex items-center gap-2 font-semibold text-sm text-designer-dark hover:text-designer-primary transition-colors w-fit focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/40 rounded"
                                        aria-label={`Learn more about ${leadProject.title} project`}
                                    >
                                        <span className="link-sweep">View case study</span>
                                        <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                                    </Link>
                                </div>
                            </article>
                        )}

                        {/* Index grid */}
                        {restProjects.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                                {restProjects.map((project, index) => (
                                    <ProjectCard key={project.id} project={project} index={pageOffset + index + 1} />
                                ))}
                            </div>
                        )}
                        </div>

                        {/* Pagination */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </>
                ) : (
                    <div className="text-center py-20 px-4">
                        <div className="max-w-md mx-auto">
                            {/* Check if it's an empty category (Coming Soon) or a failed search */}
                            {activeCategory !== "All" && !searchQuery ? (
                                <>
                                    <p className="font-mono text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">From the studio desk</p>
                                    <h3 className="mt-3 font-display text-3xl md:text-4xl font-bold text-designer-dark">Coming soon</h3>
                                    <p className="mt-4 text-gray-600 leading-relaxed">
                                        We are working on some <span className="font-semibold text-designer-dark">{activeCategory}</span> pieces
                                        worth the wait. Check back shortly.
                                    </p>
                                    <button
                                        onClick={() => setActiveCategory("All")}
                                        className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-designer-dark text-white text-sm font-semibold
                                                   hover:bg-designer-primary transition-colors focus:outline-none focus:ring-2
                                                   focus:ring-offset-2 focus:ring-designer-dark"
                                    >
                                        Browse all projects
                                        <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
                                        <FaSearch className="text-2xl text-designer-primary opacity-60" />
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl font-bold text-designer-dark">Nothing filed here</h3>
                                    <p className="mt-4 text-gray-600 leading-relaxed">
                                        {searchQuery && activeCategory !== "All"
                                            ? <>No projects match “{searchQuery}” in {activeCategory}</>
                                            : searchQuery
                                                ? <>No projects match “{searchQuery}”</>
                                                : "No projects available"
                                        }
                                    </p>
                                    <button
                                        onClick={() => {
                                            setSearchQuery("");
                                            setActiveCategory("All");
                                        }}
                                        className="mt-7 px-6 py-3 rounded-full bg-designer-primary text-white text-sm font-semibold
                                                   hover:bg-red-700 transition-colors focus:outline-none focus:ring-2
                                                   focus:ring-designer-primary focus:ring-offset-2"
                                    >
                                        Clear filters
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Full archive on Behance */}
                <div className="relative mt-14 md:mt-20 overflow-hidden rounded-[2rem] bg-designer-dark px-7 py-10 md:p-12">
                    <div className="absolute inset-0 texture-grid-dark opacity-60" aria-hidden="true" />
                    <div
                        className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-designer-primary/25 blur-[90px] pointer-events-none"
                        aria-hidden="true"
                    />
                    <div className="relative flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
                        <div className="max-w-xl">
                            <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-white/50">
                                <FaBehance className="h-4 w-4 text-white/70" aria-hidden="true" />
                                The full archive
                            </p>
                            <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                                Hungry for more? 120+ projects live on Behance
                            </h2>
                            <p className="mt-2 text-[15px] text-white/60 leading-relaxed">
                                Follow our founder Tanvirul Islam to see every brand, campaign
                                and experiment, including work too fresh for this gallery.
                            </p>
                        </div>
                        <a
                            href="https://behance.net/tanviruspixels"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-designer-dark transition-colors hover:bg-designer-primary hover:text-white"
                            aria-label="View Tanvirul Islam's full portfolio on Behance, opens in a new tab"
                        >
                            <FaBehance className="h-4 w-4" aria-hidden="true" />
                            View Behance Portfolio
                        </a>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Projects;
